import React, {forwardRef, Fragment, useEffect, useImperativeHandle, useState} from 'react';
import {Col, Row, Input, Select} from "antd"
import Form from "antd/lib/form/Form";
import {FormattedMessage} from 'react-intl';
import VideoSelection from "@components/video-contents/Selection";
import {SelectionMode} from "@models/index";
import TagSelection from "@components/tags/Selection";
import PersonSelection from '@components/people/Selection';
import CategorySelection from "@components/categories/Selection";
import {REGEX_PATTERNS} from "@constants/regex";
import {NavigationButton, NavigationConfiguration} from "@models/widget";
import {NavigationType, NavigationTypeOptions} from "@models/navigation";
import {VideoContentSortOrderOptions, VideoContentTypeOptions} from '@models/video-content';

const Option = Select.Option;


type Props = {
  navigation_button?: NavigationButton;
  required: Boolean,
  hasButtonConfig: Boolean,
  navigation_configuration: NavigationConfiguration
};


/**
 * This component handle navigation configuration and navigation button of items of all types of widget
 * Please pay attention to props and return value
 * - All validation is handled in this component
 * - Usage:
 *    -- import component
 *    --set valid props
 *    --get ref by wrappedComponentRef
 *    --call getConfig by ref anywhere you want
 *    --return value is an object or null
 *    --null: means that there is an error in the form and you should enter a valid value
 *    --You can refer to poster widget to see usage sample!
 * @param props
 * @param ref
 * @constructor
 */


function Navigation(props: Props, ref) {
  const {form, required, hasButtonConfig, navigation_button, navigation_configuration} = props;
  const {getFieldDecorator} = form;
  const [navigationButton, setNavigationButton] = useState(navigation_button || {});
  const [navigationConf, setNavigationConf] = useState(navigation_configuration || {});
  
  useEffect(() => {
  }, [navigationConf])
  
  useEffect(() => {
    setNavigationButton(props.navigation_button || {});
  }, [props.navigation_button]);
  
  
  useEffect(() => {
    setNavigationConf(props.navigation_configuration || {})
  }, [props.navigation_configuration]);
  
  
  useImperativeHandle(ref, () => ({
    getConfig: () => {
      const {form} = props;
      let returnConfig = null;
      if (navigationConf.navigation_type == NavigationType.GET_BY_FILTER) {
        /**
         * on of filter options or sort direction is required in type of filter!
         */
        form.setFieldsValue({
          'filter_options': form.getFieldValue('navigation_configuration.tag_id') ||
            form.getFieldValue('navigation_configuration.category_id') ||
            form.getFieldValue('navigation_configuration.person_id') ||
            form.getFieldValue('navigation_configuration.year') ||
            form.getFieldValue('navigation_configuration.content_type') ||
            form.getFieldValue('navigation_configuration.sort_order')
        })
      }
      
      form.validateFieldsAndScroll((err, values) => {
        if (!err) {
          returnConfig = values
        }
        console.log(err)
      });
      
      return returnConfig
    }
  }));
  
  const onChangeNavigationType = (navigation_type) => {
    setNavigationConf({
      navigation_type
    })
  };
  //
  const onChangeVideo = (content) => {
    props.form.setFieldsValue({
      'navigation_configuration.content_id': content.id
    })
  };
  
  const onChangeTag = (tag) => {
    props.form.setFieldsValue({
      'navigation_configuration.tag_id': tag.id
    })
  };
  
  const onChangeCategory = (category) => {
    props.form.setFieldsValue({
      'navigation_configuration.category_id': category.id
    })
  };
  
  const onChangePerson = (person) => {
    props.form.setFieldsValue({
      'navigation_configuration.person_id': person.id
    })
  };

  const handleIranAccessSwitch = (e) => {
    setNavigationConf({
      ...navigationConf,
        content:{
          ...navigationConf.content,
          iran_access: e
        }
      
    })
  }
  
  /**
   * Sort is only available in some navigation type
   */
  const hasSortOption = () => {
    switch (navigationConf.navigation_type) {
      case NavigationType.CONTENT:
      case NavigationType.INTERNAL_ROUTE:
      case NavigationType.EXTERNAL_ROUTE:
        return false;
      
      default:
        return true;
    }
  };
  
  const getContentBasedOnType = () => {
    switch (navigationConf.navigation_type) {
      case NavigationType.CONTENT:
        return (
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item label={<FormattedMessage id="app.related_video_content"
                                                  defaultMessage={`Related Video`}/>}>
                {getFieldDecorator("navigation_configuration.content_id", {
                  initialValue: navigationConf.content ? navigationConf.content.id : "",
                  rules: [{
                    // whitespace: true,
                    required: true,
                    message: <FormattedMessage id="app.related_video_is_required"
                                               defaultMessage={`Select related video!`}/>
                  }]
                })(<Input type={`hidden`}/>)}
                <VideoSelection mode={SelectionMode.DEFAULT}
                                initialValue={navigationConf.content}
                                onChange={onChangeVideo}/>
              </Form.Item>
            </Col>
          </Row>
        );
      
      case NavigationType.PERSON:
        return (
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label={<FormattedMessage id="app.select_person" defaultMessage={`Select Person`}/>}>
                {getFieldDecorator("navigation_configuration.person_id", {
                  initialValue: navigationConf.person ? navigationConf.person.id : "",
                  rules: [{
                    // whitespace: true,
                    required: true,
                    message: <FormattedMessage id="app.person_is_required"
                                               defaultMessage={`Select a person!`}/>
                  }]
                })(<Input type={`hidden`}/>)}
                <PersonSelection mode={SelectionMode.DEFAULT}
                                 selectedPerson={navigationConf.person}
                                 onChange={onChangePerson}/>
              </Form.Item>
            </Col>
          </Row>
        );
      case NavigationType.TAG:
        return (
          <Row>
            <Col span={24}>
              <Form.Item
                label={<FormattedMessage id="app.select_tag" defaultMessage={`Select Tag`}/>}>
                {getFieldDecorator("navigation_configuration.tag_id", {
                  initialValue: navigationConf.tag ? navigationConf.tag.id : "",
                  rules: [{
                    // whitespace: true,
                    required: true,
                    message: <FormattedMessage id="app.tag_is_required"
                                               defaultMessage={`Select tag`}/>
                  }]
                })(<Input type={`hidden`}/>)}
                <TagSelection onChange={onChangeTag}
                              initialValue={navigationConf.tag}
                              mode={SelectionMode.DEFAULT}/>
              </Form.Item>
            </Col>
          </Row>
        );
      
      case NavigationType.CATEGORY:
        return (
          <Row>
            <Col span={24}>
              <Form.Item
                label={<FormattedMessage id="app.select_category" defaultMessage={`Select Category`}/>}>
                {getFieldDecorator("navigation_configuration.category_id", {
                  initialValue: navigationConf.category ? navigationConf.category.id : "",
                  rules: [{
                    // whitespace: true,
                    required: true,
                    message: <FormattedMessage id="app.category_is_required"
                                               defaultMessage={`Select category`}/>
                  }]
                })(<Input type={`hidden`}/>)}
                <CategorySelection onChange={onChangeCategory}
                                   initialValue={navigationConf.category}
                                   mode={SelectionMode.DEFAULT}/>
              </Form.Item>
            </Col>
          </Row>
        );
      
      case NavigationType.EXTERNAL_ROUTE:
      case NavigationType.INTERNAL_ROUTE:
        return (
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item label={<FormattedMessage id="app.url" defaultMessage={`URL`}/>}>
                {getFieldDecorator("navigation_configuration.url", {
                  initialValue: navigationConf.url,
                  rules: [{
                    whitespace: true,
                    required: true,
                    message: <FormattedMessage id="app.url_is_required"
                                               defaultMessage={`URL is required!`}/>
                  }, {
                    type: "url",
                    message: <FormattedMessage id="app.invalid_url"
                                               defaultMessage={`URL is invalid!`}/>
                  }]
                })(<Input/>)}
              </Form.Item>
            </Col>
          </Row>
        );
      
      case NavigationType.GET_BY_FILTER:
        return (
          <Fragment>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label={<FormattedMessage id="app.content_type" defaultMessage={`Content Type`}/>}>
                  {getFieldDecorator("navigation_configuration.content_type", {
                    initialValue: navigationConf.content_type
                  })(
                    <Select>
                      {VideoContentTypeOptions.map((opt, index) => (
                        <Option key={index} value={opt.value}>{opt.label}</Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={<FormattedMessage id="app.year" defaultMessage={`Year`}/>}>
                  {getFieldDecorator("navigation_configuration.year", {
                    initialValue: navigationConf.year,
                    rules: [{
                      pattern: REGEX_PATTERNS.YEAR,
                      message: <FormattedMessage id="app.value_is_not_valid"
                                                 defaultMessage={`The value is not valid!`}/>
                    }]
                  })(<Input/>)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<FormattedMessage id="app.select_tag" defaultMessage={`Select Tag`}/>}>
                  {getFieldDecorator("navigation_configuration.tag_id", {
                    initialValue: navigationConf.tag ? navigationConf.tag.id : null
                  })(<Input type={`hidden`}/>)}
                  <TagSelection onChange={onChangeTag}
                                initialValue={navigationConf.tag}
                                mode={SelectionMode.DEFAULT}/>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<FormattedMessage id="app.select_category"
                                           defaultMessage={`Select Category`}/>}>
                  {getFieldDecorator("navigation_configuration.category_id", {
                    initialValue: navigationConf.category ? navigationConf.category.id : ""
                  })(<Input type={`hidden`}/>)}
                  <CategorySelection onChange={onChangeCategory}
                                     initialValue={navigationConf.category}
                                     mode={SelectionMode.DEFAULT}/>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<FormattedMessage id="app.select_person" defaultMessage={`Select Person`}/>}>
                  {getFieldDecorator("navigation_configuration.person_id", {
                    initialValue: navigationConf.person ? navigationConf.person.id : ""
                  })(<Input type={`hidden`}/>)}
                  <PersonSelection mode={SelectionMode.DEFAULT}
                                   selectedPerson={navigationConf.person}
                                   onChange={onChangePerson}/>
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item>
              {getFieldDecorator("filter_options", {
                rules: [{
                  whitespace: true,
                  required: true,
                  message: <FormattedMessage id="app.one_filter_option_or_sort_should_be_select"
                                             defaultMessage={`Sort direction or one filter option at least should be specified !`}/>
                }]
              })(<Input type={`hidden`}/>)}
            </Form.Item>
          </Fragment>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <Form>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item label={<FormattedMessage id="app.type" defaultMessage={`Type`}/>}>
            {getFieldDecorator("navigation_configuration.navigation_type", {
              initialValue: navigationConf.navigation_type,
              rules: [{
                // whitespace: true,
                required: required,
                message: <FormattedMessage id="app.navigation_is_required"
                                           defaultMessage={`Set Navigation`}/>
              }]
            })(
              <Select mode={SelectionMode.DEFAULT}
                      onChange={onChangeNavigationType}>
                {NavigationTypeOptions.map((opt, index) => (
                  <Option key={`${opt.value}_${index}`} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Col>
        
        {hasButtonConfig && (
          <Col span={12}>
            <Form.Item label={<FormattedMessage id="app.navigation_button_title"
                                                defaultMessage={`Navigation Button Title`}/>}>
              {getFieldDecorator('navigation_button.title', {
                initialValue: navigationButton.title
              })(<Input/>)}
            </Form.Item>
          </Col>
        )}
        
        {hasSortOption() && (
          <Col span={24}>
            <Form.Item label={<FormattedMessage id="app.sort_order" defaultMessage={`Sort Order`}/>}>
              {getFieldDecorator('navigation_configuration.sort_order', {
                initialValue: navigationConf.sort_order
              })(
                <Select>
                  {VideoContentSortOrderOptions.map((opt, index) => (
                    <Option key={`${opt.value}_${index}`} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
        )}
      </Row>
      {getContentBasedOnType()}
    </Form>
  )
}

export default Form.create()(forwardRef(Navigation));